// src/api/interceptors/response.interceptor.ts

/**
 * Response Interceptor
 *
 * WHY: Handle responses consistently
 * WHAT: Logging, timing, error transformation
 * BENEFIT:
 *   - Response timing for performance monitoring
 *   - Consistent success logging
 *   - Error breadcrumbs for debugging
 */

import { type AxiosResponse, AxiosError } from "axios";
import { errorLogger } from "@/core/errors/utils";
import { errorReporter } from "@/core/errors/monitoring";
/**
 * Response interceptor
 * Runs for successful responses
 */
export const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
  // Calculate request duration
  const startTime = (response.config as any).__startTime;
  const duration = startTime ? Date.now() - startTime : 0;

  // Log response
  errorLogger.debug(
    `← ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
    { duration: `${duration}ms` },
  );

  // Warn about slow requests
  if (duration > 3000) {
    errorLogger.warn(`Slow request: ${response.config.url}`, {
      duration: `${duration}ms`,
    });
  }

  // Add breadcrumb
  errorReporter.addBreadcrumb({
    category: "http",
    message: `Response ${response.status}`,
    level: "info",
    data: {
      url: response.config.url,
      status: response.status,
      duration,
    },
  });

  return response.data;
};

/**
 * Response error interceptor
 * Runs for error responses
 */
export const responseErrorInterceptor = (error: AxiosError): Promise<never> => {
  // Calculate request duration
  const startTime = (error.config as any)?.__startTime;
  const duration = startTime ? Date.now() - startTime : 0;

  // Add error breadcrumb
  errorReporter.addBreadcrumb({
    category: "http",
    message: `Error ${error.response?.status ?? "network"}`,
    level: "error",
    data: {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
      duration,
    },
  });
  // Reject with original error (will be handled by AxiosErrorHandler)
  return Promise.reject(error);
};
